interface TurnstileApi {
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  render: (
    container: HTMLElement,
    options: {
      appearance: 'interaction-only';
      callback: (token: string) => void;
      'error-callback': () => void;
      'expired-callback': () => void;
      execution: 'execute';
      sitekey: string;
    }
  ) => string;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.turnstile) {
        reject(new Error('Cloudflare Turnstile loaded without its browser API'));
        return;
      }
      resolve(window.turnstile);
    };
    script.onerror = () => reject(new Error('Cloudflare Turnstile failed to load'));
    document.head.appendChild(script);
  }).finally(() => {
    turnstileScriptPromise = null;
  });

  return turnstileScriptPromise;
};

export const getTurnstileToken = async (siteKey: string | undefined): Promise<string> => {
  if (!siteKey) {
    throw new Error('CAPTCHA is required but VITE_TURNSTILE_SITE_KEY is missing');
  }

  const turnstile = await loadTurnstile();
  const container = document.createElement('div');
  container.className = 'fixed left-1/2 top-1/2 z-[100] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-4 shadow-xl';
  document.body.appendChild(container);

  return new Promise((resolve, reject) => {
    let widgetId = '';
    const cleanup = () => {
      if (widgetId) {
        turnstile.remove(widgetId);
      }
      container.remove();
    };

    widgetId = turnstile.render(container, {
      sitekey: siteKey,
      appearance: 'interaction-only',
      execution: 'execute',
      callback: (token) => {
        cleanup();
        resolve(token);
      },
      'error-callback': () => {
        cleanup();
        reject(new Error('CAPTCHA verification failed'));
      },
      'expired-callback': () => {
        cleanup();
        reject(new Error('CAPTCHA verification expired'));
      },
    });
    turnstile.execute(widgetId);
  });
};
